<template>
  <div class="max-w-7xl mx-auto pb-10 relative">

    <!-- Contenedor con Desenfoque al Abrir Modal -->
    <div
      class="space-y-6 transition-all duration-300 animate-fade-in"
      :class="{ 'blur-sm pointer-events-none opacity-50': showModal }"
    >
      <!-- Encabezado -->
      <div class="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 class="text-2xl font-bold text-slate-900 dark:text-white tracking-tight flex items-center gap-2">
            <Radio class="w-7 h-7 text-emerald-500 animate-pulse" />
            Pedidos en Curso (Despacho Activo)
          </h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm">
            Radar en vivo de solicitudes asignadas a motorizados y paquetes rodando en calle.
          </p>
        </div>
        <button
          @click="loadData"
          class="flex items-center gap-2 px-4 py-2.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 font-bold rounded-xl transition-all cursor-pointer text-xs"
        >
          <RefreshCw class="w-4 h-4" :class="{ 'animate-spin': isLoading }" />
          <span>Actualizar Radar</span>
        </button>
      </div>

      <!-- Barra de Filtros Operativos -->
      <div class="bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm flex flex-col sm:flex-row gap-4">
        <div class="relative flex-1">
          <Search class="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            v-model="searchTerm"
            type="text"
            placeholder="Buscar por guía, cliente, motorizado, teléfono o dirección..."
            class="w-full pl-10 pr-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#F36421]/20 focus:border-[#F36421] transition-all"
          >
        </div>

        <select v-model="statusFilter" class="px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none">
          <option value="">Todos los Estados Activos</option>
          <option value="ASSIGNED">🔵 ASIGNADO (En camino a recolección)</option>
          <option value="IN_TRANSIT">🟣 EN RUTA (Hacia destino final)</option>
        </select>

        <select v-model="serviceFilter" class="px-4 py-2 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:outline-none">
          <option value="">Toda la Flota</option>
          <option value="MOTO_EXPRESS">🛵 Moto Express</option>
          <option value="CARRO_CARGA">🚗 Carro / Hatchback</option>
          <option value="CAMIONETA_B2B">🛻 Camioneta Pick-up</option>
        </select>
      </div>

      <!-- Tabla Operativa de Despachos en Curso -->
      <div class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full text-sm text-left">
            <thead class="text-xs text-slate-500 uppercase bg-slate-50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
              <tr>
                <th class="px-6 py-4 font-semibold">Guía & Fecha</th>
                <th class="px-6 py-4 font-semibold">Cliente & Contacto</th>
                <th class="px-6 py-4 font-semibold">Ruta (Origen → Destino)</th>
                <th class="px-6 py-4 font-semibold text-right">Flete / COD</th>
                <th class="px-6 py-4 font-semibold">Motorizado Asignado</th>
                <th class="px-6 py-4 font-semibold">Estado</th>
                <th class="px-6 py-4 font-semibold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-100 dark:divide-slate-800">

              <tr v-if="isLoading">
                <td colspan="7" class="px-6 py-8 text-center text-slate-500">
                  <Loader2 class="w-6 h-6 animate-spin mx-auto mb-2 text-[#F36421]" />
                  Cargando radar de pedidos activos...
                </td>
              </tr>

              <tr v-else-if="filteredList.length === 0">
                <td colspan="7" class="px-6 py-8 text-center text-slate-500">No hay pedidos en curso en este momento.</td>
              </tr>

              <tr v-else v-for="order in filteredList" :key="order.id" class="hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">

                <!-- Guía & Fecha -->
                <td class="px-6 py-4">
                  <div class="font-mono font-bold text-slate-900 dark:text-white">
                    #{{ order.tracking_code || `LLEVO-${order.id}` }}
                  </div>
                  <div class="text-[11px] text-slate-400 mt-0.5">
                    {{ formatDateTime(order.created_at) }}
                  </div>
                  <span class="inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300">
                    {{ getFleetIcon(order.service_type) }} {{ getFleetLabel(order.service_type) }}
                  </span>
                </td>

                <!-- Cliente & Contacto -->
                <td class="px-6 py-4">
                  <div class="font-bold text-slate-900 dark:text-slate-100 line-clamp-1">
                    {{ order.client_name || 'Cliente Particular' }}
                  </div>
                  <div class="text-xs text-slate-500 font-mono mt-0.5">
                    📱 {{ order.client_phone || 'Sin teléfono' }}
                  </div>
                  <div v-if="order.receiver_name" class="text-[11px] text-slate-400 mt-1 truncate max-w-[150px]">
                    Recibe: {{ order.receiver_name }}
                  </div>
                </td>

                <!-- Ruta (Origen -> Destino) -->
                <td class="px-6 py-4 max-w-[240px]">
                  <div class="space-y-1 text-xs">
                    <div class="flex items-start gap-1.5 text-slate-700 dark:text-slate-300">
                      <span class="text-emerald-500 font-bold">A:</span>
                      <span class="line-clamp-1" :title="order.pickup_address">{{ order.pickup_address }}</span>
                    </div>
                    <div class="flex items-start gap-1.5 text-slate-700 dark:text-slate-300">
                      <span class="text-red-500 font-bold">B:</span>
                      <span class="line-clamp-1" :title="order.dropoff_address">{{ order.dropoff_address }}</span>
                    </div>
                    <div class="text-[11px] font-mono text-slate-400">
                      📏 {{ order.distance_km || 0 }} km (Aprox. {{ order.duration_mins || 0 }} min)
                    </div>
                  </div>
                </td>

                <!-- Flete & COD -->
                <td class="px-6 py-4 text-right font-mono">
                  <div class="font-bold text-[#F36421] text-sm">
                    C$ {{ formatMoney(order.total_delivery_fee) }}
                  </div>
                  <div v-if="order.product_value_cod > 0" class="text-xs font-bold text-slate-700 dark:text-slate-300 mt-0.5">
                    COD: C$ {{ formatMoney(order.product_value_cod) }}
                  </div>
                  <div class="text-[10px] text-emerald-600 mt-0.5">
                    Agencia: C$ {{ formatMoney(order.llevo_net_profit || (order.total_delivery_fee * 0.25)) }}
                  </div>
                </td>

                <!-- Motorizado Asignado -->
                <td class="px-6 py-4">
                  <div class="space-y-1">
                    <div class="font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5 text-xs">
                      <UserCheck class="w-3.5 h-3.5 text-blue-500" />
                      {{ order.driver_name || 'Chofer Asignado' }}
                    </div>
                    <div class="text-[11px] font-mono text-slate-500">
                      Placa: {{ order.driver_plate || 'N/D' }}
                    </div>
                  </div>
                </td>

                <!-- Estado -->
                <td class="px-6 py-4">
                  <span
                    class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold border"
                    :class="order.status === 'ASSIGNED' ? 'bg-blue-50 text-blue-700 border-blue-200' : 'bg-purple-50 text-purple-700 border-purple-200'"
                  >
                    <span class="w-1.5 h-1.5 rounded-full" :class="order.status === 'ASSIGNED' ? 'bg-blue-500' : 'bg-purple-500 animate-pulse'"></span>
                    {{ order.status === 'ASSIGNED' ? 'Asignado' : 'En Ruta' }}
                  </span>
                </td>

                <!-- Acciones Rápida -->
                <td class="px-6 py-4 text-right">
                  <div class="flex justify-end gap-1.5">
                    <button @click="sendWhatsApp(order)" class="p-2 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" title="Enviar WhatsApp">
                      <Send class="w-4 h-4" />
                    </button>
                    <button @click="openView(order)" class="p-2 text-slate-400 hover:text-[#F36421] hover:bg-orange-50 rounded-lg transition-colors" title="Ver Expediente / Reasignar">
                      <FileText class="w-5 h-5" />
                    </button>
                  </div>
                </td>

              </tr>
            </tbody>
          </table>
        </div>

        <div class="px-6 py-4 border-t border-slate-100 dark:border-slate-800 text-xs text-slate-500 flex justify-between items-center">
          <span>Mostrando {{ filteredList.length }} pedidos en calle</span>
        </div>
      </div>
    </div>

    <!-- Formulario Modal Solo Lectura -->
    <Form
      :show="showModal"
      :orderToView="selectedItem"
      @close="showModal = false"
      @saved="loadData"
    />

  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { Search, FileText, Loader2, UserCheck, Send, Radio, RefreshCw } from 'lucide-vue-next';
import apiClient from '@/api/client';
import { useToast } from '@/composables/useToast';
import Form from './Form.vue';

const list = ref([]);
const isLoading = ref(true);
const searchTerm = ref('');
const statusFilter = ref('');
const serviceFilter = ref('');

const showModal = ref(false);
const selectedItem = ref(null);

const { showError } = useToast();

const loadData = async () => {
  isLoading.value = true;
  try {
    const res = await apiClient.get('/orders');
    const all = Array.isArray(res.data) ? res.data : (res.data.data || []);
    // FILTRADO EXCLUSIVO PARA "PEDIDOS EN CURSO"
    list.value = all.filter(o => o.status === 'ASSIGNED' || o.status === 'IN_TRANSIT');
  } catch (e) {
    showError('Error cargando los pedidos en curso.');
  } finally {
    isLoading.value = false;
  }
};

onMounted(loadData);

const filteredList = computed(() => {
  let result = list.value;
  if (statusFilter.value) result = result.filter(i => i.status === statusFilter.value);
  if (serviceFilter.value) result = result.filter(i => i.service_type === serviceFilter.value);
  if (searchTerm.value) {
    const term = searchTerm.value.toLowerCase();
    result = result.filter(i => {
      const track = (i.tracking_code || `LLEVO-${i.id}`).toLowerCase();
      const client = (i.client_name || '').toLowerCase();
      const driver = (i.driver_name || '').toLowerCase();
      const pickup = (i.pickup_address || '').toLowerCase();
      const dropoff = (i.dropoff_address || '').toLowerCase();
      return track.includes(term) || client.includes(term) || driver.includes(term) || pickup.includes(term) || dropoff.includes(term);
    });
  }
  return result;
});

const formatMoney = (val) => Number(val || 0).toLocaleString('en-US', { minimumFractionDigits: 2 });
const formatDateTime = (iso) => iso ? new Date(iso).toLocaleDateString('es-NI', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '--';

const getFleetIcon = (type) => {
  const icons = { 'MOTO_EXPRESS': '🛵', 'CARRO_CARGA': '🚗', 'CAMIONETA_B2B': '🛻' };
  return icons[type] || '📦';
};

const getFleetLabel = (type) => {
  const labels = { 'MOTO_EXPRESS': 'Moto Express', 'CARRO_CARGA': 'Carro', 'CAMIONETA_B2B': 'Camioneta' };
  return labels[type] || 'Estándar';
};

const openView = (item) => {
  selectedItem.value = item;
  showModal.value = true;
};

const sendWhatsApp = (order) => {
  const phone = (order.client_phone || '').replace(/\D/g, '');
  if (!phone) return showError('Sin teléfono registrado.');

  // Secuencias de escape Unicode universales
  const truck = '\u{1F69A}'; // 🚚
  const pin   = '\u{1F4CD}'; // 📍
  const flag  = '\u{1F3C1}'; // 🏁
  const moto  = '\u{1F6F5}'; // 🛵
  const money = '\u{1F4B0}'; // 💰

  const messageText =
    `${truck} *LLEVO | SEGUIMIENTO DE PEDIDO EN CURSO*\n` +
    `Guía: #${order.tracking_code || order.id}\n` +
    `-----------------------------------\n` +
    `${pin} *Origen:* ${order.pickup_address}\n` +
    `${flag} *Destino:* ${order.dropoff_address}\n` +
    `${moto} *Conductor:* ${order.driver_name || 'En camino'}\n` +
    `-----------------------------------\n` +
    `${money} *Flete:* C$ ${order.total_delivery_fee}\n` +
    `\n¡Tu pedido va en camino!`;

  const params = new URLSearchParams({
    phone: `505${phone}`,
    text: messageText
  });

  window.open(`https://api.whatsapp.com/send?${params.toString()}`, '_blank');
};
</script>

<style scoped>
@keyframes fade-in { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
.animate-fade-in { animation: fade-in 0.3s ease-out forwards; }
</style>